import React from "react";
import { Avatar, Box, Button, Checkbox, TextField, Tooltip, Typography, useTheme, } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { SettingCardWrapper } from "./SettingCardWrapper";
const getCompanyInitials = (name) => {
    const words = name.trim().split(" ");
    if (words.length >= 2 && words[0] && words[1]) {
        return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
};
const FieldLabel = ({ children }) => (React.createElement(Typography, { variant: "caption", sx: {
        color: "text.secondary",
        display: "block",
    } }, children));
export const CompanyConfigCard = ({ company, canUpdate, activeJob, openPickerId, setOpenPickerId, onPocToggle, onForecastMonthChange, onThresholdChange, onMarginChange, onBulkProcess, }) => {
    var _a, _b, _c, _d, _e;
    const theme = useTheme();
    return (React.createElement(SettingCardWrapper, null,
        React.createElement(Box, { sx: { display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 } },
            React.createElement(Avatar, { sx: {
                    width: 48,
                    height: 48,
                    fontSize: "14px",
                    borderRadius: "12px",
                    bgcolor: theme.palette.grey[300],
                    color: theme.palette.grey[600],
                } }, getCompanyInitials(company.name)),
            React.createElement(Box, { sx: { minWidth: 0 } },
                React.createElement(Typography, { variant: "h6", noWrap: true }, company.name),
                React.createElement(Typography, { variant: "body2", sx: {
                        color: "text.secondary",
                    } },
                    "Laatst aangepast op ",
                    company.lastUpdated))),
        React.createElement(Box, { sx: { mb: 1.5 } },
            React.createElement(FieldLabel, null, "Huidige maand"),
            React.createElement(LocalizationProvider, { dateAdapter: AdapterDayjs },
                React.createElement(DatePicker, { views: ["year", "month"], openTo: "year", open: openPickerId === company.companyId, onOpen: () => setOpenPickerId(company.companyId), onClose: () => setOpenPickerId(null), value: company.forecastDataMonth
                        ? dayjs(company.forecastDataMonth, "MMM, YYYY")
                        : null, onChange: (date) => {
                        if (date) {
                            onForecastMonthChange(company.companyId, date.format("MMM, YYYY"));
                        }
                    }, maxDate: dayjs(), disabled: !canUpdate, slotProps: {
                        textField: {
                            size: "small",
                            fullWidth: true,
                            onClick: () => {
                                if (canUpdate) {
                                    setOpenPickerId(company.companyId);
                                }
                            },
                            sx: {
                                "& .MuiOutlinedInput-root, & .MuiPickersOutlinedInput-root": {
                                    height: 40,
                                    borderRadius: "40px",
                                    cursor: "pointer",
                                    "& .MuiOutlinedInput-notchedOutline, & .MuiPickersOutlinedInput-notchedOutline": {
                                        borderColor: `rgba(145, 158, 171, 0.2)`,
                                    },
                                    "&:hover .MuiOutlinedInput-notchedOutline, &:hover .MuiPickersOutlinedInput-notchedOutline": {
                                        borderColor: `rgba(145, 158, 171, 0.2)`,
                                    },
                                },
                                "& .MuiInputBase-input, & .MuiPickersSectionList-root": {
                                    cursor: "pointer",
                                },
                            },
                        },
                    } }))),
        React.createElement(Box, { sx: { mb: 1.5 } },
            React.createElement(FieldLabel, null, "Maximale aanneemsom"),
            React.createElement(TextField, { type: "number", size: "small", fullWidth: true, value: (_a = company.thresholdAmount) !== null && _a !== void 0 ? _a : "", onChange: (e) => onThresholdChange(company.companyId, e.target.value), sx: {
                    "& .MuiOutlinedInput-root": { height: 40, borderRadius: "40px" },
                    "& .MuiInputBase-input": { borderRadius: "40px" },
                }, slotProps: {
                    input: { inputProps: { min: 0 } },
                } })),
        React.createElement(Box, { sx: { mb: 1.5 } },
            React.createElement(FieldLabel, null, "Marge %"),
            React.createElement(TextField, { type: "number", size: "small", fullWidth: true, value: (_b = company.marginPercentage) !== null && _b !== void 0 ? _b : "", onChange: (e) => onMarginChange(company.companyId, e.target.value), sx: {
                    "& .MuiOutlinedInput-root": { height: 40, borderRadius: "40px" },
                }, slotProps: {
                    input: {
                        inputProps: { min: 0, max: 100 },
                        endAdornment: (React.createElement(Typography, { variant: "body2", sx: {
                                color: "text.secondary",
                                ml: 0.5,
                            } }, "%")),
                    },
                } })),
        React.createElement(Box, { sx: { display: "flex", alignItems: "center", mb: 1.5 } },
            React.createElement(Tooltip, { title: "POC aanpassen aan-/uitzetten", arrow: true },
                React.createElement("span", null,
                    React.createElement(Checkbox, { checked: company.pocAanpassen, onChange: () => onPocToggle(company.companyId), disabled: !canUpdate, icon: React.createElement("svg", { width: "20", height: "20", viewBox: "0 0 20 20", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
                            React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M5 0C2.23858 0 0 2.23858 0 5V15C0 17.7614 2.23858 20 5 20H15C17.7614 20 20 17.7614 20 15V5C20 2.23858 17.7614 0 15 0H5ZM5.5 1.5C3.29086 1.5 1.5 3.29086 1.5 5.5V14.5C1.5 16.7091 3.29086 18.5 5.5 18.5H14.5C16.7091 18.5 18.5 16.7091 18.5 14.5V5.5C18.5 3.29086 16.7091 1.5 14.5 1.5H5.5Z", fill: theme.palette.primary.main })), checkedIcon: React.createElement("svg", { width: "20", height: "20", viewBox: "0 0 20 20", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
                            React.createElement("path", { d: "M15 0C17.7614 0 20 2.23858 20 5V15C20 17.7614 17.7614 20 15 20H5C2.23858 20 0 17.7614 0 15V5C0 2.23858 2.23858 0 5 0H15ZM14.6152 7.25488C14.2753 6.9149 13.715 6.91494 13.375 7.25488L9.24512 11.3848L7.49512 9.63477C7.15513 9.29484 6.59487 9.29484 6.25488 9.63477C5.9149 9.97475 5.91494 10.525 6.25488 10.875L8.63477 13.2451C8.80477 13.4151 9.02512 13.4951 9.24512 13.4951C9.47507 13.4951 9.69526 13.4151 9.86523 13.2451L14.6152 8.49512C14.9552 8.15513 14.9552 7.60487 14.6152 7.25488Z", fill: theme.palette.primary.main })), sx: {
                            "&.Mui-disabled svg path": {
                                fill: theme.palette.primary.light,
                            },
                        } }))),
            React.createElement(Typography, { variant: "body2" }, "POC Aanpassen")),
        activeJob ? (React.createElement(Box, { sx: {
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                gap: 0.5,
                width: "100%",
            } },
            React.createElement(Box, { sx: {
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                } },
                React.createElement(Typography, { variant: "caption", sx: { fontWeight: 600, color: theme.palette.primary.dark } }, activeJob.status === "Queued"
                    ? "In Wachtrij..."
                    : activeJob.status === "Processing"
                        ? activeJob.processedSoFar != null &&
                            activeJob.totalProjects != null
                            ? `${activeJob.processedSoFar}/${activeJob.totalProjects}`
                            : "Verwerken..."
                        : activeJob.status === "Completed"
                            ? "Voltooid"
                            : "Mislukt"),
                React.createElement(Typography, { variant: "caption", sx: { fontWeight: 500, color: theme.palette.text.secondary } }, (_c = activeJob.progressPercent) !== null && _c !== void 0 ? _c : 0,
                    "%")),
            React.createElement(Box, { sx: {
                    width: "100%",
                    height: 3,
                    borderRadius: 1.5,
                    backgroundColor: theme.palette.grey[200],
                    overflow: "hidden",
                } },
                React.createElement(Box, { sx: {
                        width: activeJob.totalProjects > 0
                            ? `${activeJob.progressPercent}%`
                            : "0%",
                        height: "100%",
                        borderRadius: 2,
                        backgroundColor: theme.palette.primary.main,
                        transition: "width 0.3s ease",
                    } })))) : (React.createElement(Button, { variant: "outlined", size: "large", fullWidth: true, onClick: () => onBulkProcess(company), disabled: !canUpdate ||
                ((!company.thresholdAmount ||
                    Number(company.thresholdAmount) === 0) &&
                    (!company.marginPercentage ||
                        Number(company.marginPercentage) === 0)), sx: {
                textTransform: "none",
                fontWeight: 500,
                fontSize: "0.8125rem",
                whiteSpace: "nowrap",
                borderRadius: "20px",
                border: "none",
                backgroundColor: (_d = theme.palette.primary[10]) !== null && _d !== void 0 ? _d : "#EFF9FF",
                color: theme.palette.primary.dark,
                py: 0.75,
                boxShadow: "none",
                "&:hover": {
                    backgroundColor: (_e = theme.palette.primary[20]) !== null && _e !== void 0 ? _e : "#BBD8EB",
                    border: "none",
                    boxShadow: "none",
                },
                "&.Mui-disabled": {
                    backgroundColor: "#919EAB33",
                    color: "#919EABCC",
                    border: "none",
                },
            } }, "Massa forecasten"))));
};
